// frontend/admin-portal/src/pages/InvoiceDetail.jsx
import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  PageHeader,
  Button,
  Badge,
  Alert,
  Spinner,
  Card,
  Panel,
  Table,
  formatMoney,
} from "@fiberhome/shared-ui";
import { invoiceService } from "../services/invoiceService";
import { paymentService } from "../services/paymentService";
import PaymentModal from "../components/sales/PaymentModal";

const STATUS_VARIANT = {
  draft: "default",
  issued: "primary",
  partial: "warning",
  paid: "success",
  overdue: "danger",
  void: "default",
};

export default function InvoiceDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [payOpen, setPayOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      setInvoice(await invoiceService.get(id));
    } catch (err) {
      setError(err.response?.data?.message || err.message);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const doIssue = async () => {
    if (!window.confirm("Issue this invoice? This will deduct stock.")) return;
    try {
      await invoiceService.issue(id);
      load();
    } catch (e) {
      alert(e.response?.data?.message || e.message);
    }
  };

  const doVoid = async () => {
    if (!window.confirm("Void this invoice?")) return;
    try {
      await invoiceService.void(id);
      load();
    } catch (e) {
      alert(e.response?.data?.message || e.message);
    }
  };

  const doDeletePayment = async (p) => {
    if (!window.confirm(`Delete payment ${p.code}?`)) return;
    try {
      await paymentService.remove(p.id);
      load();
    } catch (e) {
      alert(e.response?.data?.message || e.message);
    }
  };

  if (loading)
    return (
      <>
        <PageHeader title="Invoice" />
        <Card>
          <Spinner size="lg" />
        </Card>
      </>
    );

  if (error || !invoice)
    return (
      <>
        <PageHeader title="Not found" />
        <Alert variant="danger">{error}</Alert>
      </>
    );

  const canIssue = invoice.status === "draft";
  const canVoid = ["draft", "issued", "partial"].includes(invoice.status);
  const canPay = ["issued", "partial"].includes(invoice.status);
  const balance = Number(invoice.total) - Number(invoice.paidAmount || 0);

  return (
    <>
      <PageHeader
        title={`Invoice ${invoice.code}`}
        subtitle={invoice.customer?.name}
        breadcrumbs={["Home", "Sales", "Invoices", invoice.code]}
        actions={
          <div className="d-flex gap-2 flex-wrap">
            {canIssue && (
              <Button size="sm" variant="primary" onClick={doIssue}>
                Issue
              </Button>
            )}
            {canPay && (
              <Button
                size="sm"
                variant="success"
                onClick={() => setPayOpen(true)}
              >
                Record Payment
              </Button>
            )}
            {canVoid && (
              <Button size="sm" variant="ghost" onClick={doVoid}>
                Void
              </Button>
            )}
            <Button
              size="sm"
              variant="outline"
              onClick={() => navigate("/invoices")}
            >
              Back
            </Button>
          </div>
        }
      />

      <div className="row g-3 mb-3">
        <div className="col-md-8">
          <Panel title="Invoice Details" tone="primary">
            <div className="row g-3">
              <div className="col-md-4">
                <div className="text-muted" style={{ fontSize: 12 }}>
                  Status
                </div>
                <Badge variant={STATUS_VARIANT[invoice.status]}>
                  {invoice.status}
                </Badge>
              </div>
              <div className="col-md-4">
                <div className="text-muted" style={{ fontSize: 12 }}>
                  Issued
                </div>
                <div>{new Date(invoice.issueDate).toLocaleDateString()}</div>
              </div>
              <div className="col-md-4">
                <div className="text-muted" style={{ fontSize: 12 }}>
                  Due
                </div>
                <div>
                  {invoice.dueDate
                    ? new Date(invoice.dueDate).toLocaleDateString()
                    : "—"}
                </div>
              </div>
              <div className="col-md-4">
                <div className="text-muted" style={{ fontSize: 12 }}>
                  Subtotal
                </div>
                <div>
                  {formatMoney(invoice.subtotal, { symbol: invoice.currency })}
                </div>
              </div>
              <div className="col-md-4">
                <div className="text-muted" style={{ fontSize: 12 }}>
                  Tax
                </div>
                <div>
                  {formatMoney(invoice.taxTotal, { symbol: invoice.currency })}
                </div>
              </div>
              <div className="col-md-4">
                <div className="text-muted" style={{ fontSize: 12 }}>
                  Total
                </div>
                <div style={{ fontWeight: 700 }}>
                  {formatMoney(invoice.total, { symbol: invoice.currency })}
                </div>
              </div>
              <div className="col-md-4">
                <div className="text-muted" style={{ fontSize: 12 }}>
                  Paid
                </div>
                <div>
                  {formatMoney(invoice.paidAmount, {
                    symbol: invoice.currency,
                  })}
                </div>
              </div>
              <div className="col-md-4">
                <div className="text-muted" style={{ fontSize: 12 }}>
                  Balance
                </div>
                <div
                  style={{
                    fontWeight: 700,
                    color: balance > 0 ? "#dc3545" : "#198754",
                  }}
                >
                  {formatMoney(balance, { symbol: invoice.currency })}
                </div>
              </div>
            </div>
          </Panel>
        </div>
        <div className="col-md-4">
          <Panel title="Customer" tone="info">
            <div style={{ fontWeight: 600 }}>{invoice.customer?.name}</div>
            <div className="text-muted" style={{ fontSize: 12.5 }}>
              {invoice.customer?.email}
            </div>
            <div className="text-muted" style={{ fontSize: 12.5 }}>
              {invoice.customer?.phone}
            </div>
          </Panel>
        </div>
      </div>

      <Panel title="Line Items" tone="primary" noBodyPadding>
        <Table
          columns={[
            { key: "description", label: "Description" },
            {
              key: "quantity",
              label: "Qty",
              render: (r) => `${r.quantity} ${r.unit}`,
            },
            {
              key: "unitPrice",
              label: "Price",
              render: (r) =>
                formatMoney(r.unitPrice, { symbol: invoice.currency }),
            },
            { key: "taxRate", label: "Tax", render: (r) => `${r.taxRate}%` },
            {
              key: "lineTotal",
              label: "Total",
              render: (r) => (
                <span style={{ fontWeight: 600 }}>
                  {formatMoney(r.lineTotal, { symbol: invoice.currency })}
                </span>
              ),
            },
          ]}
          data={invoice.items || []}
          emptyMessage="No items"
          rowKey="id"
        />
      </Panel>

      <div className="mt-3">
        <Panel title="Payments" tone="success" noBodyPadding>
          <Table
            columns={[
              {
                key: "code",
                label: "Payment",
                render: (r) => (
                  <span style={{ fontFamily: "monospace" }}>{r.code}</span>
                ),
              },
              {
                key: "method",
                label: "Method",
                render: (r) => <Badge>{r.method}</Badge>,
              },
              {
                key: "amount",
                label: "Amount",
                render: (r) => (
                  <span style={{ fontWeight: 600 }}>
                    {formatMoney(r.amount, { symbol: r.currency })}
                  </span>
                ),
              },
              {
                key: "paidAt",
                label: "Paid",
                render: (r) => new Date(r.paidAt).toLocaleString(),
              },
              {
                key: "reference",
                label: "Ref",
                render: (r) => r.reference || "—",
              },
              {
                key: "actions",
                label: "",
                width: 80,
                render: (r) => (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => doDeletePayment(r)}
                  >
                    Delete
                  </Button>
                ),
              },
            ]}
            data={invoice.payments || []}
            emptyMessage="No payments yet"
            rowKey="id"
          />
        </Panel>
      </div>

      <PaymentModal
        open={payOpen}
        invoice={invoice}
        onClose={() => setPayOpen(false)}
        onSaved={() => {
          setPayOpen(false);
          load();
        }}
      />
    </>
  );
}
